import { createSlice, nanoid, PayloadAction } from '@reduxjs/toolkit'
import {
  type ListType as CoreListType,
  type OptionType as CoreOptionType,
  type ReasonType,
  SideEnum,
  WeightEnum,
} from 'pcl-core'
import { track } from 'track'

export type { ReasonType }
export { SideEnum, WeightEnum }

/**
 * Interface for the Option type
 * @interface
 * @property {string} id - The ID of the option
 * @property {string} label - The label of the option
 * @property {ReasonType[]} reasons - The reasons for the option
 */
export interface OptionType extends CoreOptionType {
  reasons?: ReasonType[]
}

/**
 * Interface for the List type
 * @interface
 * @property {ReasonType[]} reasons - The reasons in the list
 * @property {OptionType[]} options - The options in the list
 * @property {MetaType} meta - The metadata of the list
 */
export interface ListType extends Omit<CoreListType, 'options'> {
  options: OptionType[]
  meta: MetaType
}

/**
 * Interface for the Meta type, which is metadata for the list
 * @interface
 * @property {string} key - The key of the saved list (for persistence)
 * @property {string} fileName - The filename of the list
 * @property {number} savedAt - The timestamp when the list was saved
 * @property {number} updatedAt - The timestamp when the list was updated
 * @property {string} color - The color of the list item for the SaveList component
 * @property {boolean} [isSharedList] - Whether the list was loaded from a shared list page without user modifications
 */
export interface MetaType {
  key?: string
  fileName?: string
  savedAt?: number
  updatedAt?: number
  color?: string
  initials?: string
  lastAddedOptionId?: string
  generationError?: {
    optionId: string
    message: string
  }
  isGenerating?: boolean
  aiDisabled?: boolean
  isSharedList?: boolean
}

/**
 * Function to get the local state
 * @param {ListType} defaultState - The default state
 * @returns {ListType} The local state
 */
function getLocalState(defaultState: ListType) {
  return defaultState
}

// Initial state of the list
const initialState: ListType = getLocalState({
  reasons: [],
  options: [],
  meta: {},
})

// Options

/**
 * Slice for the options
 * @type {Slice}
 */
export const optionSlice = createSlice({
  name: 'option',
  initialState: initialState.options,
  reducers: {
    // Add an option
    addOption: (state, action) => {
      track('event', 'add-option')
      const newId = action.payload.id || nanoid(4)
      state.push({
        label: action.payload.label,
        id: newId,
      })
      return state
    },
    // Delete an option
    deleteOption: (state, action: PayloadAction<{ id: string }>) => {
      track('event', 'delete-option')
      return state.filter(
        (item: { id: string }) => item.id !== action.payload.id,
      )
    },
    // Edit an option
    editOption: (
      state,
      action: PayloadAction<{ id: string; label: string }>,
    ) => {
      track('event', 'edit-option')
      return state.map((item) => {
        if (item.id === action.payload.id) {
          return { ...item, label: action.payload.label }
        } else {
          return item
        }
      })
    },
    // Clear all options
    clearOptions: () => {
      track('event', 'clear-options')
      return []
    },
    // Replace the state of the options
    replaceOptionsState: (state, action: PayloadAction<OptionType[]>) => {
      return action.payload
    },
  },
})

// Export the actions and the reducer of the options
export const {
  addOption,
  deleteOption,
  editOption,
  clearOptions,
  replaceOptionsState,
} = optionSlice.actions

export const optionsReducer = optionSlice.reducer

// Reasons

/**
 * Slice for the reasons
 * @type {Slice}
 */
export const reasonSlice = createSlice({
  name: 'reason',
  initialState: initialState.reasons,
  reducers: {
    // Add a reason
    addReason: (state, action) => {
      track('event', 'add-reason', {
        event_label: action.payload.side,
      })
      state.push({
        label: action.payload.label,
        optionId: action.payload.optionId,
        id: nanoid(4),
        side: action.payload.side,
        weight: WeightEnum.LOW,
      })
    },
    // Add multiple reasons at once
    addReasons: (state, action: PayloadAction<Omit<ReasonType, 'id'>[]>) => {
      track('event', 'add-ai-reasons', {
        event_label: action.payload.length.toString(),
      })
      action.payload.forEach((reason) => {
        state.push({
          ...reason,
          id: nanoid(4),
          aiGenerated: true,
        })
      })
    },
    // Delete a reason
    deleteReason: (state, action: PayloadAction<{ id: string }>) => {
      track('event', 'delete-reason')
      return state.filter(
        (item: Partial<ReasonType>) => item.id !== action.payload.id,
      )
    },
    // Edit the weight of a reason
    editReasonWeight: (
      state,
      action: PayloadAction<{ id: string; weight: WeightEnum }>,
    ) => {
      track('event', 'edit-reason-weight')
      return state.map((item: ReasonType) => {
        if (item.id === action.payload.id) {
          return { ...item, weight: action.payload.weight }
        } else {
          return item
        }
      })
    },
    // Edit a reason
    editReason: (
      state,
      action: PayloadAction<{ id: string; label: string }>,
    ) => {
      track('event', 'edit-reason')
      return state.map((item: ReasonType) => {
        if (item.id === action.payload.id) {
          return { ...item, label: action.payload.label }
        } else {
          return item
        }
      })
    },
    // Replace the state of the reasons
    replaceReasonsState: (state, action: PayloadAction<ReasonType[]>) => {
      return action.payload
    },
  },
})

// Export the actions and the reducer of the reasons
export const {
  addReason,
  addReasons,
  deleteReason,
  editReason,
  editReasonWeight,
  replaceReasonsState,
} = reasonSlice.actions

export const reasonsReducer = reasonSlice.reducer

/**
 * Slice for the metadata
 * @type {Slice}
 */
export const metaSlice = createSlice({
  name: 'meta',
  initialState: initialState.meta,
  reducers: {
    // Replace the state of the metadata
    replaceMetaState: (state, action: PayloadAction<Partial<MetaType>>) => {
      return { ...state, ...action.payload }
    },
    // Set the last added option ID for focus management
    setLastAddedOptionId: (state, action: PayloadAction<string>) => {
      return { ...state, lastAddedOptionId: action.payload }
    },
    // Clear the last added option ID
    clearLastAddedOptionId: (state) => {
      return { ...state, lastAddedOptionId: undefined }
    },
    // Set generation error for an option
    setGenerationError: (
      state,
      action: PayloadAction<{ optionId: string; message: string }>,
    ) => {
      return { ...state, generationError: action.payload }
    },
    // Clear generation error
    clearGenerationError: (state) => {
      return { ...state, generationError: undefined }
    },
    // Set generating state
    setIsGenerating: (state, action: PayloadAction<boolean>) => {
      return { ...state, isGenerating: action.payload }
    },
    // Set AI disabled state
    setAiDisabled: (state, action: PayloadAction<boolean>) => {
      return { ...state, aiDisabled: action.payload }
    },
    // Clear the metadata
    clearMetaState: () => {
      return {}
    },
  },
  extraReducers: (builder) => {
    const clearSharedList = (state: MetaType) => {
      state.isSharedList = false
    }
    builder
      .addCase(addOption, clearSharedList)
      .addCase(editOption, clearSharedList)
      .addCase(deleteOption, clearSharedList)
      .addCase(clearOptions, clearSharedList)
      .addCase(addReason, clearSharedList)
      .addCase(addReasons, clearSharedList)
      .addCase(editReason, clearSharedList)
      .addCase(editReasonWeight, clearSharedList)
      .addCase(deleteReason, clearSharedList)
  },
})

// Export the action and the reducer of the metadata
export const {
  replaceMetaState,
  setLastAddedOptionId,
  clearLastAddedOptionId,
  setGenerationError,
  clearGenerationError,
  setIsGenerating,
  setAiDisabled,
  clearMetaState,
} = metaSlice.actions

export const metaReducer = metaSlice.reducer

export interface SettingsType {
  askBeforeDiscardingUnsavedChanges: boolean
  autoSave: boolean
}

export const settingsSlice = createSlice({
  name: 'settings',
  initialState: {
    askBeforeDiscardingUnsavedChanges:
      process.env.FEATURES_DEFAULT_ASK_BEFORE_DISCARDING === 'true',
    autoSave: process.env.FEATURES_DEFAULT_AUTO_SAVE === 'true',
  } as SettingsType,
  reducers: {
    setAskBeforeDiscardingUnsavedChanges: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      return { ...state, askBeforeDiscardingUnsavedChanges: action.payload }
    },
    setAutoSave: (state, action: PayloadAction<boolean>) => {
      return { ...state, autoSave: action.payload }
    },
  },
})

export const { setAskBeforeDiscardingUnsavedChanges, setAutoSave } =
  settingsSlice.actions
export const settingsReducer = settingsSlice.reducer
